import React, { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { http } from '../services/http';

/**
 * investment 안 경로만 허용한다 — 외부 주소 · `//host` · 역슬래시면 버리고 기본 화면으로.
 * 로그인 · 바로 이동 화면으로 되돌아가는 것도 막는다(GRK_project#133 과 같다).
 */
export function safeNext(next: string | null): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.includes('\\')) return '/dashboard';
  if (/^\/(login|sso)(\/|\?|#|$)/.test(next)) return '/dashboard';
  return next;
}

/**
 * admin 바로 이동 받기(handbook #16) — `/sso?code=…&next=…`.
 * 표는 1분 · 한 번만이라, 받자마자 주소에서 지우고 백엔드에 확인을 맡긴다.
 * 실패하면 로그인 화면에 「admin 에서 다시 이동해 주세요」. 직접 로그인은 그대로다.
 */
export const SsoPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return; // 개발 모드(StrictMode)에서 두 번 돌아 표를 두 번 쓰지 않게
    started.current = true;
    const code = params.get('code');
    const next = safeNext(params.get('next'));
    window.history.replaceState(null, '', '/sso');
    const fail = (message: string) => navigate('/login', { replace: true, state: { message } });
    if (!code) { fail('바로 이동 정보가 없습니다. admin 에서 다시 이동해 주세요'); return; }

    (async () => {
      try {
        const r = await http.post<{ accessToken: string }>('/auth/sso', { code });
        await login(r.accessToken);
        navigate(next, { replace: true });
      } catch (e) {
        fail((e as Error).message || '바로 이동이 확인되지 않았습니다. admin 에서 다시 이동해 주세요');
      }
    })();
  }, [params, navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center gap-2 text-sm text-slate-500">
      <Loader2 size={16} className="animate-spin" /> admin 에서 이동하는 중…
    </div>
  );
};
